import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Mirrors the Phase 1 range tabs so the UX feels consistent across analytics.
const RANGES = [
  { v: "today", l: "Today" },
  { v: "7d",    l: "7 Days" },
  { v: "30d",   l: "30 Days" },
  { v: "90d",   l: "90 Days" },
  { v: "all",   l: "All Time" },
];

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}

function StatTile({ label, value, accent, helper, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#0E0E0E] border border-[#2A2A2A] p-4 hover:border-[#FF1E1E]/40 transition-colors"
    >
      <div className="text-[10px] font-mono-data tracking-widest text-white/40 uppercase mb-2">{label}</div>
      <div className={`font-display text-2xl leading-none font-extrabold ${accent ? "text-[#FF1E1E]" : "text-white"}`}>
        {value}
      </div>
      {helper && <div className="text-[10px] font-mono-data text-white/35 mt-2 tracking-wide">{helper}</div>}
    </div>
  );
}

export default function RegistrationPrefixAnalytics() {
  const [range, setRange] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mobile: show top 8 prefixes by default; "View More" reveals the rest.
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics/registration-prefix?range=${range}`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => toast.error("Failed to load registration prefix analytics"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [range]);

  const top = data?.top_prefixes || [];
  const shown = expanded || top.length <= 8 ? top : top.slice(0, 8);
  const moreCount = Math.max(0, top.length - 8);

  return (
    <section className="mb-8" data-testid="registration-prefix-analytics">
      {/* Header + range tabs — same pattern as other phases */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="qd-overline">Registration Prefix</div>
          <div className="text-[11px] text-white/45 mt-1">
            Hyderabad-priority vs district leads · conversion rate · top RTOs by volume.
            {data?.total_leads !== undefined && (
              <span className="text-white/30 ml-2">· {data.total_leads} leads in range</span>
            )}
          </div>
        </div>
        <div className="inline-flex border border-[#2A2A2A] bg-[#0E0E0E] overflow-x-auto max-w-full" role="tablist" data-testid="rp-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.v}
              data-testid={`rp-range-${r.v}`}
              onClick={() => setRange(r.v)}
              className={`px-2.5 sm:px-3 py-2 text-[10px] font-mono-data tracking-widest uppercase border-r border-[#2A2A2A] last:border-r-0 transition-colors whitespace-nowrap ${
                range === r.v ? "bg-[#FF1E1E]/15 text-[#FF1E1E]" : "text-white/55 hover:text-white"
              }`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="qd-card p-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
          Loading registration analytics…
        </div>
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2A2A] mb-5">
            <StatTile
              label="Hyderabad Leads"
              value={data?.hyderabad_leads ?? "—"}
              accent
              helper={fmtPct(data?.hyderabad_pct)}
              testid="rp-hyd-leads"
            />
            <StatTile
              label="District Leads"
              value={data?.district_leads ?? "—"}
              helper={fmtPct(data?.district_pct)}
              testid="rp-dist-leads"
            />
            <StatTile
              label="Conv Rate – Hyderabad"
              value={fmtPct(data?.hyderabad_conversion_rate)}
              helper="won / leads"
              testid="rp-hyd-conv"
            />
            <StatTile
              label="Conv Rate – District"
              value={fmtPct(data?.district_conversion_rate)}
              helper="won / leads"
              testid="rp-dist-conv"
            />
          </div>

          {/* Top prefixes table */}
          <div className="qd-card p-0 overflow-hidden" data-testid="rp-table-card">
            <div className="px-4 sm:px-5 py-4 border-b border-[#1A1A1A] flex items-baseline justify-between">
              <div>
                <div className="qd-overline">Top Registration Prefixes</div>
                <div className="text-[11px] text-white/45 mt-1">By lead volume · purchases column shows acquisition wins</div>
              </div>
              {data?.unknown_leads > 0 && (
                <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/35">
                  {data.unknown_leads} unknown
                </div>
              )}
            </div>

            {top.length === 0 ? (
              <div className="px-5 py-10 text-center text-xs text-white/40 font-mono-data tracking-wide">
                No leads with registration prefixes in this range.
              </div>
            ) : (
              <>
                <table className="w-full text-xs font-mono-data" data-testid="rp-table">
                  <thead>
                    <tr className="text-[10px] tracking-widest uppercase text-white/40 border-b border-[#1A1A1A]">
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Prefix</th>
                      <th className="px-3 py-2 text-left">District</th>
                      <th className="px-3 py-2 text-left">Region</th>
                      <th className="px-3 py-2 text-right">Leads</th>
                      <th className="px-3 py-2 text-right">Purchases</th>
                      <th className="px-3 py-2 text-right">Conv %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r, i) => (
                      <tr
                        key={r.prefix}
                        className="border-b border-[#141414] hover:bg-white/[0.02]"
                        data-testid={`rp-row-${r.prefix}`}
                      >
                        <td className="px-3 py-2 text-white/45 w-10">#{i + 1}</td>
                        <td className="px-3 py-2 text-white font-bold">{r.prefix}</td>
                        <td className="px-3 py-2 text-white font-display normal-case">
                          {r.district || <span className="text-white/30">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          {r.is_hyderabad_priority ? (
                            <span className="text-[10px] uppercase tracking-widest text-[#FF1E1E] border border-[#FF1E1E]/40 px-1.5 py-0.5">
                              Priority
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-widest text-white/40 border border-white/10 px-1.5 py-0.5">
                              District
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-white">{r.count}</td>
                        <td className="px-3 py-2 text-right text-white">{r.won_count}</td>
                        <td className={`px-3 py-2 text-right ${r.conversion_rate > 0 ? "text-emerald-400" : "text-white/40"}`}>
                          {fmtPct(r.conversion_rate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {moreCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    data-testid="rp-toggle"
                    className="w-full px-5 py-3 text-[10px] font-mono-data tracking-widest uppercase text-[#FF1E1E] border-t border-[#1A1A1A] hover:bg-[#FF1E1E]/5 transition-colors"
                  >
                    {expanded ? "Show Top 8" : `View ${moreCount} more`}
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}
